import OfflineStore from "./OfflineStore";

/**
 * PrecacheConfig — Central configuration for cache-primary mode.
 *
 * Global defaults can be overridden at the catalog level via
 * OfflineStore.SetSyncMetadata("syncConfig", { ... })
 */

/**
 * Cache version - increment this when making breaking changes to:
 * - Precache item structure (attribs, filters, pages, etc.)
 * - How we store/interpret cached data
 * - API response format changes
 *
 * When version changes, existing caches will be invalidated and rebuilt.
 */
export const PRECACHE_VERSION = 7; // v7: Streaming index build + sharded persistence for 250k+ items

/**
 * Check if the cached data is from a different version and needs rebuilding.
 * @param {string|number} catalogId
 * @returns {Promise<{needsRebuild: boolean, currentVersion: number, cachedVersion: number|null}>}
 */
export async function checkCacheVersion(catalogId) {
	const store = OfflineStore.ForCatalog(catalogId);
	const cachedVersion = await store.GetSyncMetadata("cacheVersion");

	return {
		needsRebuild: cachedVersion !== null && cachedVersion !== PRECACHE_VERSION,
		currentVersion: PRECACHE_VERSION,
		cachedVersion,
	};
}

/**
 * Store the current cache version after a successful sync.
 * @param {string|number} catalogId
 */
export async function setCacheVersion(catalogId) {
	const store = OfflineStore.ForCatalog(catalogId);
	await store.SetSyncMetadata("cacheVersion", PRECACHE_VERSION);
}

// Global defaults
const DEFAULTS = {
	// How often to check for updates (24 hours)
	deltaSyncInterval: 24 * 60 * 60 * 1000,

	// When to show "data may be stale" indicator (6 hours)
	staleThreshold: 6 * 60 * 60 * 1000,

	// Number of items to fetch in priority first-page (display order)
	priorityFetchCount: 60,

	// Item count threshold for progressive sync mode
	largeAccountThreshold: 10000,

	// Max storage before LRU eviction (MB)
	maxStorageMB: 100,

	// Pause between batches for large syncs (ms)
	progressiveSyncPause: 100,
};

/**
 * Get the merged configuration for a catalog.
 * Catalog-level overrides take precedence over global defaults.
 * @param {string|number} catalogId
 * @returns {Promise<Object>} Merged config
 */
export async function getCatalogSyncConfig(catalogId) {
	const store = OfflineStore.ForCatalog(catalogId);
	const catalogConfig = await store.GetSyncMetadata("syncConfig");
	return { ...DEFAULTS, ...catalogConfig };
}

/**
 * Set catalog-level configuration overrides.
 * @param {string|number} catalogId
 * @param {Object} config - Partial config to override
 */
export async function setCatalogSyncConfig(catalogId, config) {
	const store = OfflineStore.ForCatalog(catalogId);
	const existing = await store.GetSyncMetadata("syncConfig") || {};
	await store.SetSyncMetadata("syncConfig", { ...existing, ...config });
}

/**
 * Check if precache-primary mode should be active.
 * Always true after initial sync (no user toggle).
 * @param {string|number} catalogId
 * @returns {Promise<boolean>}
 */
export async function isPrecachePrimary(catalogId) {
	const store = OfflineStore.ForCatalog(catalogId);
	const itemCount = await store.GetPrecacheItemCount();
	return itemCount > 0;
}

/**
 * Check if the cached data is stale (exceeds staleThreshold).
 * @param {string|number} catalogId
 * @returns {Promise<{isStale: boolean, age: number, threshold: number}>}
 */
export async function checkStaleness(catalogId) {
	const config = await getCatalogSyncConfig(catalogId);
	const store = OfflineStore.ForCatalog(catalogId);
	const lastSync = await store.GetSyncMetadata("lastSyncTime");

	if (!lastSync) {
		return { isStale: true, age: Infinity, threshold: config.staleThreshold };
	}

	const age = Date.now() - new Date(lastSync).getTime();
	return {
		isStale: age > config.staleThreshold,
		age,
		threshold: config.staleThreshold,
	};
}

/**
 * Track a catalog visit for usage-based sync frequency.
 * Call this when user views the catalog.
 * @param {string|number} catalogId
 */
export async function trackCatalogVisit(catalogId) {
	const store = OfflineStore.ForCatalog(catalogId);
	const visits = await store.GetSyncMetadata("visitTimestamps") || [];
	const now = Date.now();

	// Keep only last 30 days of visits
	const thirtyDaysAgo = now - (30 * 24 * 60 * 60 * 1000);
	const recentVisits = visits.filter(t => t > thirtyDaysAgo);

	// Add current visit (but dedupe if within 1 hour of last visit)
	const lastVisit = recentVisits[recentVisits.length - 1] || 0;
	if (now - lastVisit > 60 * 60 * 1000) {
		recentVisits.push(now);
	}

	await store.SetSyncMetadata("visitTimestamps", recentVisits);
}

/**
 * Get usage-adjusted sync interval based on visit frequency.
 * More frequent users get more frequent syncs.
 * @param {string|number} catalogId
 * @returns {Promise<number>} Adjusted interval in ms
 */
export async function getAdjustedSyncInterval(catalogId) {
	const config = await getCatalogSyncConfig(catalogId);
	const store = OfflineStore.ForCatalog(catalogId);
	const visits = await store.GetSyncMetadata("visitTimestamps") || [];

	const now = Date.now();
	const oneWeekAgo = now - (7 * 24 * 60 * 60 * 1000);
	const visitsThisWeek = visits.filter(t => t > oneWeekAgo).length;

	// Adjust interval based on usage:
	// 7+ visits/week (daily user) → 6 hours
	// 3-6 visits/week (regular user) → 12 hours
	// 1-2 visits/week → 24 hours (default)
	// 0 visits/week → 24 hours (default)
	if (visitsThisWeek >= 7) {
		return Math.min(config.deltaSyncInterval, 6 * 60 * 60 * 1000); // 6 hours
	} else if (visitsThisWeek >= 3) {
		return Math.min(config.deltaSyncInterval, 12 * 60 * 60 * 1000); // 12 hours
	}

	return config.deltaSyncInterval; // Default (24 hours)
}

/**
 * Check if a delta sync is due (exceeds deltaSyncInterval).
 * Uses usage-adjusted interval for frequent users.
 * @param {string|number} catalogId
 * @returns {Promise<{isDue: boolean, age: number, interval: number}>}
 */
export async function isDeltaSyncDue(catalogId) {
	const interval = await getAdjustedSyncInterval(catalogId);
	const store = OfflineStore.ForCatalog(catalogId);
	const lastSync = await store.GetSyncMetadata("lastSyncTime");

	if (!lastSync) {
		return { isDue: false, age: 0, interval };
	}

	const age = Date.now() - new Date(lastSync).getTime();
	return {
		isDue: age >= interval,
		age,
		interval,
	};
}

/**
 * Check if this catalog qualifies as a "large account".
 * @param {string|number} catalogId
 * @returns {Promise<boolean>}
 */
export async function isLargeCatalog(catalogId) {
	const config = await getCatalogSyncConfig(catalogId);
	const store = OfflineStore.ForCatalog(catalogId);
	const itemCount = await store.GetPrecacheItemCount();
	return itemCount >= config.largeAccountThreshold;
}

// Export defaults for reference
export const PRECACHE_DEFAULTS = DEFAULTS;

export default {
	PRECACHE_VERSION,
	checkCacheVersion,
	setCacheVersion,
	getCatalogSyncConfig,
	setCatalogSyncConfig,
	isPrecachePrimary,
	checkStaleness,
	isDeltaSyncDue,
	isLargeCatalog,
	trackCatalogVisit,
	getAdjustedSyncInterval,
	DEFAULTS: PRECACHE_DEFAULTS,
};
